// Adding a clerk statement and an individual fee slip from the Clerk Fee Slips page.
//
// Both used to be done only in Deadline Guard's Client Account dashboard. They save to the
// same Deadline Guard records this page already reads (through the signed /api/render
// proxy), so nothing new is stored and the firm sees the result on its own page.
//
// A statement is shown to a firm by its client_firm_label, compared with the firm's portal
// name ("LACW" -> "lacw", src/clerk-statement-access.js in the backend). The statement reader
// guesses the firm from the PDF's text, and guesses "Greensill and Associates" for RGA, which
// matches no portal. So a statement uploaded here is labelled with the firm whose page it was
// uploaded on, and the page says so first if the PDF names a different firm.

export const CLIENT_FIRMS = {
  lacw: { label: "LACW", name: "Law and Advocacy Centre for Women" },
  vals: { label: "VALS", name: "Victorian Aboriginal Legal Service" },
  rga: { label: "RGA", name: "Robyn Greensill & Associates" },
  eas: { label: "EAS", name: "EAS Legal" },
};

// The clerk lists Deadline Guard accepts for a fee slip (FEE_SLIP_CLERKS in the backend's
// server.js). Anything else is refused there.
export const FEE_SLIP_CLERKS = ["Parnells", "Holmes", "Meldrums", "Devers", "Greens", "Chapmans", "Pattersons", "Foleys"];

export const FEE_SLIP_STATUSES = [
  { value: "not_paid", label: "Not paid" },
  { value: "partial_paid", label: "Partly paid" },
  { value: "paid", label: "Paid" },
];

// The portal's API takes at most about 4.5 MB per request, and a PDF grows by a third when
// it is sent as text.
export const MAX_UPLOAD_BYTES = 3 * 1024 * 1024;

export function uploadTooLarge(file) {
  return Number(file?.size || 0) > MAX_UPLOAD_BYTES;
}

export function isPdf(file) {
  return file?.type === "application/pdf" || /\.pdf$/i.test(String(file?.name || ""));
}

// The firm the PDF itself names, when it is not the firm whose page this is.
export function statementFirmMismatch(statement, brand) {
  const firm = CLIENT_FIRMS[brand];
  const named = String(statement?.client_firm_label || "").trim();
  if (!firm || !named) return "";
  const sameFirm = named.toUpperCase() === firm.label
    || (brand === "rga" && /greensill/i.test(named));
  return sameFirm ? "" : named;
}

// What is saved for a statement read from a PDF: the reader's clerk, date and rows, filed
// under this page's firm.
export function statementRecord(statement = {}, brand, filename = "") {
  const firm = CLIENT_FIRMS[brand];
  if (!firm) throw new Error("This page has no firm to file the statement under.");
  const clerk = String(statement.clerk_name || statement.firm || "").trim();
  if (!clerk) throw new Error("The clerk could not be read from this statement.");
  return {
    firm: clerk,
    clerk_name: clerk,
    client_firm: firm.name,
    client_firm_label: firm.label,
    statement_label: `${clerk} - ${firm.label} statement`,
    as_of: statement.as_of || null,
    filename: filename || statement.filename || null,
    rows: Array.isArray(statement.rows) ? statement.rows : [],
  };
}

export function statementTotal(rows = []) {
  return rows.reduce((sum, row) => {
    const amount = Number(String(row?.outstanding_amount ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("The PDF could not be read."));
    reader.readAsDataURL(file);
  });
}

async function postJson(url, body, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error || payload?.message || `The request returned ${response.status}.`);
  }
  return payload;
}

// Clerk statements are only reached through the signing proxy's /api/render/ path, as the
// page reads them (src/lib/clerkStatementRecords.js). Unlike /api/fee-slips there is no
// shorter address for them: /api/clerk-statements/... is not routed and returns 404.
export const CLERK_STATEMENTS_API = "/api/render/api/clerk-statements";

// Reads a statement PDF into the clerk, date and rows, without saving anything.
export async function readClerkStatement(file, fetchImpl) {
  const dataUrl = await readFileAsDataUrl(file);
  const payload = await postJson(`${CLERK_STATEMENTS_API}/parse-upload`, {
    filename: file.name,
    mime_type: file.type || "application/pdf",
    data: dataUrl.replace(/^data:[^,]*,/, ""),
  }, fetchImpl);
  if (!Array.isArray(payload?.statement?.rows)) throw new Error("The statement could not be read.");
  return { ...payload.statement, filename: file.name };
}

export function saveClerkStatement(record, fetchImpl) {
  return postJson(`${CLERK_STATEMENTS_API}/record`, record, fetchImpl);
}

export async function addFeeSlip(apiBase, { brand, clientName, clerkName, counselName, amount, invoiceNumber, status, notes, file }, fetchImpl) {
  if (!clientName?.trim()) throw new Error("Enter the client's name.");
  if (!FEE_SLIP_CLERKS.includes(clerkName)) throw new Error("Choose the clerk list.");
  if (!file || !isPdf(file)) throw new Error("Choose the fee slip PDF.");
  if (uploadTooLarge(file)) throw new Error("That PDF is over 3 MB. Save a smaller copy and try again.");
  const fileData = await readFileAsDataUrl(file);
  return postJson(`${apiBase}/api/fee-slips`, {
    client_brand: brand,
    client_name: clientName.trim(),
    clerk_name: clerkName,
    counsel_name: counselName?.trim() || "",
    amount: amount?.trim() || "",
    invoice_number: invoiceNumber?.trim() || "",
    status: status || "not_paid",
    notes: notes?.trim() || "",
    file_name: file.name,
    file_size: file.size,
    file_data: fileData.startsWith("data:application/pdf;base64,") ? fileData : fileData.replace(/^data:[^,]*,/, "data:application/pdf;base64,"),
  }, fetchImpl);
}
